import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { fetchEventById, assetUrl } from '../services/api';
import { CATEGORY_EMOJI } from './Events';
import logo from '../assets/logo.png';
import PhotoViewer from '../components/PhotoViewer';
import { linkifyText } from '../utils/linkify';
import './MandalDetail.css';
import './Events.css';

function formatDate(value) {
  if (!value) return '';
  return new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [viewer, setViewer] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    fetchEventById(id)
      .then((data) => { if (!cancelled) setEvent(data); })
      .catch(() => { if (!cancelled) setNotFound(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <div className="detail-not-found">
        <div className="container" style={{ textAlign: 'center', padding: '200px 0 100px' }}>
          <img src={logo} alt="Aarambh" style={{ height: '80px', objectFit: 'contain', marginBottom: '8px' }} />
          <h2>Loading event...</h2>
        </div>
      </div>
    );
  }

  if (notFound || !event) {
    return (
      <div className="detail-not-found">
        <div className="container" style={{ textAlign: 'center', padding: '200px 0 100px' }}>
          <img src={logo} alt="Aarambh" style={{ height: '80px', objectFit: 'contain', marginBottom: '8px' }} />
          <h2>Event Not Found</h2>
          <p>We couldn't find this event.</p>
          <Link to="/events" className="btn-primary" style={{ marginTop: '20px', display: 'inline-block' }}>
            ← Back to Events
          </Link>
        </div>
      </div>
    );
  }

  const emoji = CATEGORY_EMOJI[event.category] || '🎉';

  return (
    <div className="mandal-detail-page">
      {/* Hero */}
      <div className="detail-hero">
        <div className="container">
          <button className="back-btn" onClick={() => navigate(-1)}>
            ← Back to Events
          </button>
          <div className="detail-hero-content">
            <div className="detail-hero-icon">
              <span style={{ fontSize: '3rem' }}>{emoji}</span>
            </div>
            <div className="detail-hero-info">
              <span className={`event-status-badge ${event.isUpcoming ? 'upcoming' : 'past'}`}>
                {event.isUpcoming ? '🟢 Upcoming' : '✅ Completed'}
              </span>
              <h1>{event.title}</h1>
              {event.category && <p className="detail-tagline">{event.category}</p>}
              <div className="detail-meta-row">
                <span>📅 {formatDate(event.date)}</span>
                {event.time && <span>⏰ {event.time}</span>}
                {event.location && <span>📍 {event.location}</span>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* About */}
      <section className="detail-about-section">
        <div className="container">
          <div className="badge">Event Details</div>
          <h2>About this Event</h2>
          {event.description ? (
            event.description.split('\n\n').map((para, i) => <p key={i}>{linkifyText(para)}</p>)
          ) : (
            <p>No further details have been added for this event yet.</p>
          )}
        </div>
      </section>

      {/* Gallery */}
      <section className="detail-gallery-section">
        <div className="container">
          <div className="section-header">
            <div className="badge">Photos</div>
            <h2>Event Gallery</h2>
            <div className="divider">
              <div className="divider-line" />
              <span className="divider-icon">🌸</span>
              <div className="divider-line right" />
            </div>
          </div>
          {event.images && event.images.length > 0 ? (
            <div className="gallery-grid">
              {event.images.map((img, i) => (
                <div
                  key={img.id}
                  className="gallery-item"
                  onClick={() => setViewer({ images: event.images.map((im) => ({ url: assetUrl(im.url), caption: im.caption })), index: i })}
                  style={{ cursor: 'pointer' }}
                >
                  <img src={assetUrl(img.url)} alt={img.caption || event.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  {img.caption && <div className="gallery-overlay">{img.caption}</div>}
                </div>
              ))}
            </div>
          ) : (
            <p className="gallery-note">📸 Photos coming soon for this event.</p>
          )}
        </div>
      </section>

      {viewer && (
        <PhotoViewer
          images={viewer.images}
          index={viewer.index}
          onClose={() => setViewer(null)}
          onNavigate={(d) => setViewer((v) => ({ ...v, index: (v.index + d + v.images.length) % v.images.length }))}
        />
      )}

      {/* Bottom CTA */}
      <section className="detail-bottom-cta">
        <div className="container" style={{ textAlign: 'center' }}>
          <h2>Stay Connected with Aarambh</h2>
          <p>Explore more community events and celebrations happening across Valsad.</p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', marginTop: '24px', flexWrap: 'wrap' }}>
            <Link to="/events" className="btn-outline">← View All Events</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
